import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import type { PermissionName } from '../../constants/permissions';
import type { UserRole } from '../../types/auth.types';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: PermissionName[];
  requiredOriginalRoles?: UserRole[];
  /** If true, user needs ANY of the permissions. If false (default), needs ALL */
  requireAny?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredOriginalRoles = [],
  requireAny = false
}) => {
  const {
    isAuthenticated,
    loading,
    permissionsLoading,
    user,
    originalUser,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuth();
  const location = useLocation();

  if (loading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permission-based access — no role hierarchy, purely permissions
  if (requiredPermissions.length > 0) {
    const hasAccess = requireAny 
      ? hasAnyPermission(requiredPermissions)
      : hasAllPermissions(requiredPermissions);

    if (!hasAccess) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  if (requiredOriginalRoles.length > 0) {
    const originalRole = originalUser?.role ?? user?.originalRole ?? user?.role;
    if (!originalRole || !requiredOriginalRoles.includes(originalRole)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};
